import { Popover } from "@base-ui/react/popover"
import React from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { IconSize } from "shared/types/Icon"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { useTeamMemberByEmail } from "entities/team-members/model/useTeamMemberByEmail"
import { TeamMember } from "entities/team-members/types/TeamMember"
import { TeamRole } from "entities/team-members/types/TeamRole"
import { TeamMemberChip } from "entities/team-members/ui/team-member-chip/TeamMemberChip"
import { TeamMemberList } from "entities/team-members/ui/team-member-list/TeamMemberList"
import { Team } from "entities/teams/types/Team"
import { TeamInitialsList } from "entities/teams/ui/team-initials-list/TeamInitialsList"

import { ProjectTeamPopoverProps } from "../ProjectTeamPopover"
import "../styles/_project-team-popover.scss"

export interface ProjectTeamPopoverViewModel extends ProjectTeamPopoverProps {
  open?: Popover.Root.Props["open"]
  onOpenChange?: Popover.Root.Props["onOpenChange"]
  currentUserMember?: TeamMember | null | undefined
}

const ProjectTeamPopoverButton = React.memo(
  ({
    email,
    team,
    className
  }: {
    email: string | null | undefined
    team: Team
    className?: string
  }) => {
    const { t } = useTranslation()
    const teamMember = useTeamMemberByEmail({
      email,
      teamMembers: team.memberships
    })

    return (
      <Popover.Trigger
        className={classNames(
          "btn btn-secondary project-team-popover-button",
          className
        )}
        data-testid="project-team-popover-trigger"
        render={
          <button
            type="button"
            aria-label={t("ProjectTeam.ProjectTeamPopoverButtonLabel", {
              role: t(`Team.Role.${teamMember?.role as TeamRole}`)
            })}
          />
        }
      >
        {teamMember ? (
          <div className="project-team-popover-button__current-user">
            <TeamMemberChip teamMember={teamMember} />
          </div>
        ) : null}
        <div className="project-team-popover-button__team-initials-list">
          <TeamInitialsList
            team={team}
            ignoredUserEmails={email ?? undefined}
          />
        </div>
        <Icon
          className="project-team-popover-button__indicator"
          name={IconName.SMALL_CARET_DOWN}
          size={IconSize.XXS}
        />
      </Popover.Trigger>
    )
  }
)

const ProjectTeamPopoverViewComponent = React.memo(
  ({
    team,
    popoverClassName,
    defaultOpen = false,
    email,
    open,
    className,
    onOpenChange,
    onManage
  }: ProjectTeamPopoverViewModel) => {
    const { t } = useTranslation()

    if (!team) {
      return null
    }

    return (
      <Popover.Root
        defaultOpen={defaultOpen}
        open={open}
        onOpenChange={onOpenChange}
      >
        <ProjectTeamPopoverButton
          team={team}
          email={email}
          className={className}
        />

        <Popover.Portal>
          <Popover.Positioner
            sideOffset={16}
            collisionPadding={16}
            side="bottom"
            align="end"
            positionMethod="fixed"
            className="base-ui-positioner"
          >
            <Popover.Popup
              className={classNames(
                "base-ui-popover project-team-popover__popup",
                popoverClassName
              )}
            >
              <Popover.Title className="project-team-popover__title">
                {t("ProjectTeam.CurrentPermissions")}
              </Popover.Title>
              <div className="project-team-popover__content">
                <TeamMemberList teamMembers={team.memberships} isReadonly />
                {onManage ? (
                  <div className="project-team-popover__manage-button-container">
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      onClick={onManage}
                      className="project-team-popover__manage-button"
                      data-testid="project-team-popover__manage-button"
                    >
                      {t("ProjectTeam.Actions.Edit")}
                    </Button>
                  </div>
                ) : null}
              </div>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    )
  }
)

export const ProjectTeamPopoverView = React.memo(
  ProjectTeamPopoverViewComponent
) as typeof ProjectTeamPopoverViewComponent
